// Cross-tab session synchronisation over BroadcastChannel (per-origin, so
// per-app automatically). Tabs tell each other about logouts, refreshes and
// user activity so one active tab keeps its siblings alive and one logout
// logs every tab out.

export type SessionMessage =
  | { type: "LOGOUT"; reason: "user" | "expired"; at: number }
  | { type: "TOKEN_REFRESHED"; at: number }
  | { type: "ACTIVITY"; at: number }
  | { type: "STAY_LOGGED_IN"; at: number };

const CHANNEL_NAME = "prx-session";

const channel: BroadcastChannel | null =
  typeof BroadcastChannel !== "undefined"
    ? new BroadcastChannel(CHANNEL_NAME)
    : null;

// Omit must distribute over the union (plain Omit collapses it to the
// common properties and drops 'reason').
type DistributiveOmit<T, K extends keyof any> = T extends any ? Omit<T, K> : never;

export function postSessionMessage(
  msg: DistributiveOmit<SessionMessage, "at">
): void {
  try {
    channel?.postMessage({ ...msg, at: Date.now() });
  } catch {
    /* channel closed — nothing to sync */
  }
}

export function subscribeSessionMessages(
  handler: (msg: SessionMessage) => void
): () => void {
  if (!channel) return () => {};
  const listener = (event: MessageEvent) => {
    if (event?.data?.type) handler(event.data as SessionMessage);
  };
  channel.addEventListener("message", listener);
  return () => channel.removeEventListener("message", listener);
}
